import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLiveKit } from '../hooks/useLiveKit';
import { useThemeClasses } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import axios from 'axios';
import {
  Radio,
  Plus,
  Mic,
  MicOff,
  PhoneOff,
  Users,
  Volume2,
  AlertCircle,
  Wifi,
  WifiOff,
  Loader2,
  Settings,
  Music,
  ChevronDown
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Attaches a MediaStream to a <video> element via ref
function VideoTile({ stream }) {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
}

export default function StoopPage() {
  const { user } = useAuth();
  const { isDark, textClass, textMutedClass, textVeryMutedClass, borderClass, hoverBgClass, bgActiveClass, hoverTextClass } = useThemeClasses();
  const [stoops, setStoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newStoopTitle, setNewStoopTitle] = useState('');
  const [activeStoopId, setActiveStoopId] = useState(null);
  const [activeStoopData, setActiveStoopData] = useState(null);
  const [activeSpeakers, setActiveSpeakers] = useState([]);
  const [micError, setMicError] = useState(null);
  const [speakerQueue, setSpeakerQueue] = useState([]);
  const [showMicRequestMenu, setShowMicRequestMenu] = useState(false);
  const [hasRequestedMic, setHasRequestedMic] = useState(false);

  // Story Time state
  const [storyMode, setStoryMode] = useState(false);
  const [storyLines, setStoryLines] = useState([]);
  const [storyInput, setStoryInput] = useState('');
  const [hasTurn, setHasTurn] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [storyForks, setStoryForks] = useState([]);
  const [showExplainer, setShowExplainer] = useState(
    () => !localStorage.getItem('blvx_storytime_seen')
  );
  const storyScrollRef = useRef(null);
  const chatScrollRef = useRef(null);

  // Expression options for requesting the mic
  const MIC_EXPRESSIONS = [
    { id: "got_next", text: "I got next", emoji: "🏀" },
    { id: "mic_me", text: "Mic me", emoji: "🎤" },
    { id: "lemme_speak", text: "Lemme speak", emoji: "🗣️" },
    { id: "got_heat", text: "I got heat", emoji: "🔥" },
    { id: "step_up", text: "Step up", emoji: "✊" },
    { id: "real_quick", text: "Real quick", emoji: "💯" },
  ];
  
  // LiveKit hook
  const {
    isConnected,
    connectionState,
    participants,
    isMuted,
    isSpeaker,
    connectionError,
    audioBlocked,
    audioMode,
    availableDevices,
    selectedDeviceId,
    isCameraOn,
    videoTracks,
    connect: connectLiveKit,
    disconnect: disconnectLiveKit,
    toggleMute,
    toggleCamera,
    resumeAudio,
    debugRoomState,
    switchAudioDevice,
    setAudioMode,
    enumerateDevices,
  } = useLiveKit({
    stoopId: activeStoopId,
    userId: user?.user_id,
    onParticipantJoined: (data) => {
      console.log('[Stoop] Participant joined:', data);
      toast.success(`${data.name} joined the Stoop`);
      if (activeStoopId) {
        fetchStoopData(activeStoopId);
      }
    },
    onParticipantLeft: (data) => {
      console.log('[Stoop] Participant left:', data.user_id);
      if (activeStoopId) {
        fetchStoopData(activeStoopId);
      }
    },
    onSpeakingChange: (speakerIds) => {
      setActiveSpeakers(speakerIds);
    }
  });

  useEffect(() => {
    fetchStoops();
  }, []);

  // Store connect function in ref to avoid dependency issues
  const connectRef = useRef(connectLiveKit);
  useEffect(() => {
    connectRef.current = connectLiveKit;
  }, [connectLiveKit]);

  // Connect LiveKit when joining a stoop - only trigger on activeStoopId change
  useEffect(() => {
    if (activeStoopId && user?.user_id) {
      console.log('[Stoop] Connecting LiveKit for stoop:', activeStoopId);
      connectRef.current();
    }
  }, [activeStoopId, user?.user_id]);

  // Poll speaker queue when in active stoop
  useEffect(() => {
    if (!activeStoopId) return;
    
    const interval = setInterval(() => {
      fetchSpeakerQueue(activeStoopId);
    }, 3000); // Every 3 seconds
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStoopId]);

  const fetchStoops = async () => {
    try {
      const response = await axios.get(`${API}/api/stoop/live`, { withCredentials: true });
      setStoops(response.data);
    } catch (error) {
      console.error('Error fetching stoops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoopData = async (stoopId) => {
    try {
      const response = await axios.get(`${API}/api/stoop/${stoopId}`, { withCredentials: true });
      setActiveStoopData(response.data);
      
      // Also fetch speaker queue
      fetchSpeakerQueue(stoopId);
    } catch (error) {
      console.error('Error fetching stoop data:', error);
    }
  };

  const fetchSpeakerQueue = async (stoopId) => {
    try {
      const response = await axios.get(`${API}/api/stoop/${stoopId}/queue`, { withCredentials: true });
      setSpeakerQueue(response.data.queue || []);
      // Check if current user is in queue
      setHasRequestedMic(response.data.queue?.some(req => req.user_id === user?.user_id) || false);
    } catch (error) {
      console.error('Error fetching speaker queue:', error);
    }
  };

  const requestMic = async (expression) => {
    if (!activeStoopId) return;
    
    try {
      const response = await axios.post(
        `${API}/api/stoop/${activeStoopId}/request-mic?expression=${expression}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setHasRequestedMic(true);
      setShowMicRequestMenu(false);
      fetchSpeakerQueue(activeStoopId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request mic');
    }
  };

  const cancelMicRequest = async () => {
    if (!activeStoopId) return;
    
    try {
      await axios.delete(`${API}/api/stoop/${activeStoopId}/request-mic`, { withCredentials: true });
      toast.success('Request cancelled');
      setHasRequestedMic(false);
      fetchSpeakerQueue(activeStoopId);
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const approveSpeaker = async (userId) => {
    if (!activeStoopId) return;
    
    try {
      await axios.post(
        `${API}/api/stoop/${activeStoopId}/approve-speaker/${userId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Speaker approved!');
      fetchStoopData(activeStoopId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve speaker');
    }
  };

  const denySpeaker = async (userId) => {
    if (!activeStoopId) return;
    
    try {
      await axios.post(
        `${API}/api/stoop/${activeStoopId}/deny-speaker/${userId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Request denied');
      fetchSpeakerQueue(activeStoopId);
    } catch (error) {
      toast.error('Failed to deny speaker');
    }
  };

  const createStoop = async () => {
    if (!newStoopTitle.trim()) return;
    
    try {
      const response = await axios.post(`${API}/api/stoop/create`, {
        title: newStoopTitle.trim()
      }, { withCredentials: true });
      
      setStoops([response.data, ...stoops]);
      setCreateOpen(false);
      setNewStoopTitle('');
      toast.success('Stoop started!');
      
      // Auto-join the stoop
      joinStoop(response.data.stoop_id);
    } catch (error) {
      toast.error('Failed to create Stoop');
    }
  };

  const joinStoop = async (stoopId) => {
    try {
      await axios.post(`${API}/api/stoop/${stoopId}/join`, {}, { withCredentials: true });
      const response = await axios.get(`${API}/api/stoop/${stoopId}`, { withCredentials: true });
      setActiveStoopId(stoopId);
      setActiveStoopData(response.data);
      setMicError(null);
    } catch (error) {
      toast.error('Failed to join Stoop');
    }
  };

  const leaveStoop = async () => {
    if (!activeStoopId) return;
    
    // Disconnect LiveKit
    await disconnectLiveKit();
    
    try {
      await axios.post(`${API}/api/stoop/${activeStoopId}/leave`, {}, { withCredentials: true });
      setActiveStoopId(null);
      setActiveStoopData(null);
      setActiveSpeakers([]);
      fetchStoops();
    } catch (error) {
      toast.error('Failed to leave Stoop');
    }
  };

  const endStoop = async () => {
    if (!activeStoopId || activeStoopData?.host_id !== user.user_id) return;
    
    // Disconnect LiveKit
    await disconnectLiveKit();
    
    try {
      await axios.post(`${API}/api/stoop/${activeStoopId}/end`, {}, { withCredentials: true });
      setActiveStoopId(null);
      setActiveStoopData(null);
      setActiveSpeakers([]);
      fetchStoops();
      toast.success('Stoop ended');
    } catch (error) {
      toast.error('Failed to end Stoop');
    }
  };

  const handleToggleMic = useCallback(async () => {
    if (!isSpeaker) {
      toast.error('You need to be a speaker to use the mic');
      return;
    }
    
    setMicError(null);
    
    try {
      await toggleMute();
      
      if (isMuted) {
        toast.success('Microphone activated - You\'re live!');
      } else {
        toast.success('Microphone muted');
      }
    } catch (error) {
      console.error('[Stoop] Mic toggle error:', error);
      let errorMsg = 'Microphone access denied. Check your browser settings.';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMsg = 'Microphone access denied. Check your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMsg = 'No microphone found. Please connect a microphone.';
      } else if (error.name === 'NotReadableError') {
        errorMsg = 'Microphone is already in use by another application.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setMicError(errorMsg);
      toast.error(errorMsg, {
        icon: <AlertCircle className="h-4 w-4" />,
        duration: 5000,
      });
    }
  }, [isSpeaker, isMuted, toggleMute]);

  // Check if a speaker is currently live (speaking)
  const isSpeakerLive = (speakerUserId) => {
    if (speakerUserId === user.user_id) {
      return !isMuted;
    }
    // Check if they're in the active speakers list from LiveKit
    return activeSpeakers.includes(speakerUserId);
  };
  
  // Determine if local user has mic active
  const isLocalMicActive = !isMuted;

  // Connection status indicator
  const getConnectionStatus = () => {
    switch (connectionState) {
      case 'connected':
        return { icon: <Wifi className="h-3 w-3 text-green-500" />, text: 'Connected' };
      case 'connecting':
        return { icon: <Loader2 className="h-3 w-3 text-yellow-500 animate-spin" />, text: 'Connecting...' };
      case 'reconnecting':
        return { icon: <Loader2 className="h-3 w-3 text-yellow-500 animate-spin" />, text: 'Reconnecting...' };
      default:
        return { icon: <WifiOff className="h-3 w-3 text-red-500" />, text: 'Disconnected' };
    }
  };

  const connectionStatus = getConnectionStatus();

  // Story Time helpers
  const enterStoryMode = () => {
    const host = activeStoopData?.host;
    const hostName = host?.name || 'The Host';
    const hostInitials = hostName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    setStoryLines([{
      id: 1,
      authorName: hostName,
      authorHandle: '@' + (host?.username || 'host'),
      authorInitials: hostInitials,
      authorGradient: 'linear-gradient(135deg,#F07B1D,#C9950C)',
      num: '#1 · Opening',
      text: 'It started the way most things start — quietly, without announcement, like the first note of a song nobody asked for but everybody needed.',
      plates: 0,
      plated: false,
      isOpening: true,
    }]);
    setChatMessages([
      { id: 1, av: 'AV', gradient: 'linear-gradient(135deg,#18B574,#2F9FC8)', name: '@bellalove', text: 'story time!!!! 📖', hype: true },
      { id: 2, av: 'RK', gradient: 'linear-gradient(135deg,#7C4FD4,#D44F2E)', name: '@rk', text: 'let\'s go' },
    ]);
    setStoryForks([]);
    setStoryMode(true);
    // Give user the stick after 4s for demo
    setTimeout(() => setHasTurn(true), 4000);
  };

  const exitStoryMode = () => {
    setStoryMode(false);
    setStoryLines([]);
    setChatMessages([]);
    setStoryForks([]);
    setStoryInput('');
    setHasTurn(false);
  };

  const submitStoryLine = () => {
    if (!storyInput.trim()) return;
    const newLine = {
      id: storyLines.length + 1,
      authorName: user?.name || 'You',
      authorHandle: '@' + (user?.username || 'you'),
      authorInitials: (user?.name || 'Y').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
      authorGradient: 'linear-gradient(135deg,#2F9FC8,#18B574)',
      num: `#${storyLines.length + 1}`,
      text: storyInput.trim(),
      plates: 0,
      plated: false,
    };
    setStoryLines(prev => [...prev, newLine]);
    setStoryInput('');
    setHasTurn(false);
    toast.success('Line dropped 🪄 The story grows');
    setTimeout(() => {
      storyScrollRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
    }, 100);
  };

  const plateLine = (lineId) => {
    setStoryLines(prev => prev.map(l =>
      l.id === lineId ? { ...l, plates: l.plated ? l.plates - 1 : l.plates + 1, plated: !l.plated } : l
    ));
  };

  const voteFork = (forkId, optIdx) => {
    setStoryForks(prev => prev.map(f => f.id === forkId
      ? { ...f, options: f.options.map((o, i) => ({ ...o, voted: i === optIdx })), closed: true }
      : f
    ));
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    setChatMessages(prev => [...prev, {
      id: prev.length + 1,
      av: (user?.name || 'Y').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
      gradient: 'linear-gradient(135deg,#F07B1D,#C9950C)',
      name: '@' + (user?.username || 'you'),
      text: chatInput.trim(),
    }]);
    setChatInput('');
    setTimeout(() => { chatScrollRef.current?.scrollTo({ top: 99999, behavior: 'smooth' }); }, 50);
  };

  const dropStoryToBlock = () => {
    toast.success('📖 Story dropped to The Block — all contributors credited');
    exitStoryMode();
  };

  const handleStartStory = () => {
    enterStoryMode();
    setHasTurn(true);
  };

  // Gradient pool for contributors
  const GRADIENTS = [
    'linear-gradient(135deg,#F07B1D,#C9950C)',
    'linear-gradient(135deg,#2F9FC8,#18B574)',
    'linear-gradient(135deg,#D44F2E,#7C4FD4)',
    'linear-gradient(135deg,#18B574,#2F9FC8)',
    'linear-gradient(135deg,#7C4FD4,#D44F2E)',
  ];

  return (
    <div className="mb-safe" data-testid="stoop-page">
      {/* Header — BLVX topbar with Live / Story Time mode toggle */}
      <div style={{ background: '#090B10', borderBottom: '1px solid rgba(255,255,255,0.055)', position: 'sticky', top: 0, zIndex: 30 }}>
        <div style={{ height: '52px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px' }}>
          {/* Logo */}
          <a href="/home" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#EAE0CC', textDecoration: 'none' }}>
            BL<span style={{ color: '#F07B1D' }}>VX</span>
          </a>
          <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.09)' }} />
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#72788A', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Home</span>
            <span style={{ color: '#3A3F4E' }}>›</span>
            <span style={{ color: '#F07B1D' }}>The Stoop</span>
          </div>

          {/* Live / Story Time pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: '8px', background: '#151A24', border: '1px solid rgba(255,255,255,0.055)', borderRadius: '8px', padding: '3px' }}>
            <button
              onClick={() => storyMode ? exitStoryMode() : undefined}
              style={{
                fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '.5px',
                color: !storyMode ? '#18B574' : '#72788A',
                background: !storyMode ? '#1C2230' : 'transparent',
                padding: '5px 12px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                transition: 'all .14s', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {!storyMode && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#18B574', animation: 'pip 1.8s ease-in-out infinite', display: 'inline-block' }} />}
              Live
            </button>
            <button
              onClick={() => !storyMode ? enterStoryMode() : undefined}
              style={{
                fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '.5px',
                color: storyMode ? '#A855F7' : '#72788A',
                background: storyMode ? '#1C2230' : 'transparent',
                padding: '5px 12px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                transition: 'all .14s',
              }}
            >
              Story Time
            </button>
          </div>

          {/* Start stoop */}
          <div style={{ marginLeft: 'auto' }}>
            <Button
              onClick={() => setCreateOpen(true)}
              size="sm"
              className={cn("text-xs", isDark ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90")}
              data-testid="create-stoop-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Start
            </Button>
          </div>
        </div>
      </div>

      {/* ── STORY TIME VIEW — top level (works with or without active stoop) ── */}
      {storyMode && (
        <div className="story-shell" style={{ height: 'calc(100dvh - 52px)' }}>
          <div className="story-main">
            {/* Story header */}
            <div className="story-hdr">
              <div className="sh-top">
                <div className="sh-icon">📖</div>
                <div>
                  <div className="sh-title">{activeStoopData?.title || 'Story Time'}</div>
                  <div className="sh-sub">Story Time · {storyLines.length} line{storyLines.length !== 1 ? 's' : ''} · {(activeStoopData?.listeners?.length || 0) + 1} on The Stoop</div>
                </div>
              </div>
              <div className="sh-contributors">
                {storyLines.slice(0, 5).map((l, i) => (
                  <div key={l.id} className="contrib-av" style={{ background: GRADIENTS[i % GRADIENTS.length] }}>
                    {l.authorInitials}
                  </div>
                ))}
                {storyLines.length > 5 && <span className="contrib-more">+{storyLines.length - 5} contributors</span>}
              </div>
            </div>

            {/* Story scroll */}
            <div className="story-scroll" ref={storyScrollRef}>
              {/* Onboarding explainer card */}
              {showExplainer && (storyLines.length < 3 || !localStorage.getItem('blvx_storytime_seen')) && (
                <div style={{
                  margin: '16px 20px',
                  background: 'linear-gradient(135deg, rgba(168,85,247,.1), rgba(240,123,29,.06))',
                  border: '1px solid rgba(168,85,247,.25)',
                  borderRadius: '14px',
                  padding: '20px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '28px' }}>📖</span>
                    <div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '1.5px', color: '#EAE0CC', lineHeight: 1 }}>
                        Welcome to Story Time
                      </div>
                      <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: '#A855F7', textTransform: 'uppercase', marginTop: '2px' }}>
                        Collaborative storytelling on The Stoop
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                    {[
                      { icon: '✍🏾', step: '1', text: 'One person writes one line at a time — then passes the stick to the next writer.' },
                      { icon: '⑂', step: '2', text: 'At story forks, the community votes on what direction the story goes next.' },
                      { icon: '🍽', step: '3', text: "When the story's done, drop it to The Block for the whole community to read." },
                    ].map(item => (
                      <div key={item.step} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(168,85,247,.15)', border: '1px solid rgba(168,85,247,.25)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: "'Bebas Neue', sans-serif", fontSize: '13px', color: '#A855F7',
                        }}>
                          {item.step}
                        </div>
                        <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#C8BCA8', margin: 0, paddingTop: '4px' }}>
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        localStorage.setItem('blvx_storytime_seen', 'true');
                        setShowExplainer(false);
                        document.getElementById('join-queue-btn')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      style={{
                        flex: 1, background: '#A855F7', color: '#fff',
                        fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px', letterSpacing: '1.5px',
                        border: 'none', borderRadius: '8px', padding: '11px',
                        cursor: 'pointer', boxShadow: '0 4px 16px rgba(168,85,247,.3)',
                      }}
                    >
                      ✍🏾 Join the Story Queue
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('blvx_storytime_seen', 'true');
                        setShowExplainer(false);
                      }}
                      style={{
                        padding: '11px 16px', background: 'transparent',
                        border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px',
                        color: '#72788A', fontFamily: "'Azeret Mono', monospace",
                        fontSize: '10px', letterSpacing: '1px', cursor: 'pointer',
                      }}
                    >
                      Just watching
                    </button>
                  </div>
                </div>
              )}

              {/* Empty story state */}
              {storyLines.length === 0 && !showExplainer && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', textAlign: 'center' }}>
                  <span style={{ fontSize: '48px', marginBottom: '16px' }}>📖</span>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: '#EAE0CC', marginBottom: '8px' }}>
                    No Story Running Yet
                  </div>
                  <p style={{ fontSize: '14px', color: '#72788A', lineHeight: 1.6, maxWidth: '280px', marginBottom: '24px' }}>
                    Be the one who starts it. The Stoop is quiet — someone has to open with the first line.
                  </p>
                  <button
                    onClick={handleStartStory}
                    style={{
                      background: '#A855F7', color: '#fff',
                      fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px', letterSpacing: '2px',
                      border: 'none', borderRadius: '10px', padding: '13px 28px',
                      cursor: 'pointer', boxShadow: '0 4px 20px rgba(168,85,247,.35)',
                    }}
                  >
                    + Start a Story
                  </button>
                </div>
              )}

              {storyLines.map((line, idx) => (
                <div key={line.id}>
                  {idx > 0 && idx % 3 === 0 && (
                    <div className="story-divider">
                      <span>ROUND {Math.ceil(idx / 3)}</span>
                    </div>
                  )}
                  <div className="story-line">
                    <div className="sl-av" style={{ background: GRADIENTS[(line.id - 1) % GRADIENTS.length] }}>
                      {line.authorInitials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sl-meta">
                        <span className="sl-name">{line.authorName}</span>
                        <span className="sl-handle">{line.authorHandle}</span>
                        <span className="sl-num">{line.num}</span>
                      </div>
                      <div className={cn("sl-text", line.isOpening && "opening")}>{line.text}</div>
                      <div className="sl-plates">
                        <button className={cn("sl-plate-btn", line.plated && "plated")} onClick={() => plateLine(line.id)}>
                          🍽 {line.plates}
                        </button>
                        {line.isOpening && <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: 9, color: 'var(--char)' }}>Opening line</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Fork cards */}
              {storyForks.map(fork => (
                <div key={fork.id} className="fork-card">
                  <div className="fork-lbl">⑂ THE FORK · Community Vote</div>
                  <div className="fork-question">{fork.question}</div>
                  <div className="fork-options">
                    {fork.options.map((opt, i) => (
                      <div key={i} className={cn("fork-opt", opt.voted && "voted")} onClick={() => !fork.closed && voteFork(fork.id, i)}>
                        <div className="fo-letter">{String.fromCharCode(65 + i)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="fo-text">{opt.text}</div>
                          <div className="fo-bar"><div className="fo-fill" style={{ width: `${opt.pct}%` }} /></div>
                        </div>
                        <div className="fo-votes">{opt.votes} votes</div>
                      </div>
                    ))}
                  </div>
                  {!fork.closed && (
                    <div className="fork-timer">
                      <span style={{ color: 'var(--story)' }}>Voting closes soon</span>
                      <div className="ft-bar"><div className="ft-fill" style={{ width: '60%' }} /></div>
                    </div>
                  )}
                </div>
              ))}

              {/* Stick indicator */}
              {!hasTurn && storyLines.length > 0 && (
                <div className="stick-indicator">
                  <div className="si-av" style={{ background: GRADIENTS[storyLines.length % GRADIENTS.length] }}>
                    {storyLines[storyLines.length - 1]?.authorInitials}
                    <div className="si-glow" />
                  </div>
                  <div className="si-text">
                    <div className="si-name">{storyLines[storyLines.length - 1]?.authorName} has the stick</div>
                    <div className="si-sub" style={{ color: '#F07B1D' }}>WRITING NOW...</div>
                  </div>
                  <span style={{ fontSize: '18px', animation: 'stickFloat 1.5s ease-in-out infinite alternate' }}>🎤</span>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="story-input-area">
              {hasTurn ? (
                <div style={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.055)', background: '#090B10', padding: '16px 24px' }}>
                  <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '3px', textTransform: 'uppercase', color: '#F07B1D', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px', animation: 'flash .8s ease-in-out infinite alternate' }}>
                    🎤 YOUR TURN — ADD TO THE STORY
                  </div>
                  <div style={{ position: 'relative' }}>
                    <textarea
                      value={storyInput}
                      onChange={e => setStoryInput(e.target.value)}
                      placeholder="Continue the story... make it yours"
                      style={{
                        width: '100%', background: '#151A24',
                        border: '1px solid rgba(240,123,29,.2)', borderRadius: '12px',
                        padding: '14px 110px 14px 16px',
                        fontFamily: "'DM Serif Display', serif", fontSize: '15px', fontStyle: 'italic',
                        color: '#EAE0CC', outline: 'none', resize: 'none', lineHeight: 1.5, minHeight: '60px',
                        transition: 'border-color .15s, box-shadow .15s', boxSizing: 'border-box',
                      }}
                      onFocus={e => { e.target.style.borderColor = '#F07B1D'; e.target.style.boxShadow = '0 0 0 3px rgba(240,123,29,.1)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(240,123,29,.2)'; e.target.style.boxShadow = 'none'; }}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitStoryLine(); } }}
                    />
                    <div style={{ position: 'absolute', right: '8px', bottom: '8px', display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => { setHasTurn(false); }}
                        style={{ padding: '7px 12px', borderRadius: '7px', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1px', color: '#72788A', background: '#1C2230', border: '1px solid rgba(255,255,255,0.055)', cursor: 'pointer' }}
                      >
                        PASS
                      </button>
                      <button
                        onClick={submitStoryLine}
                        style={{ padding: '7px 20px', borderRadius: '7px', fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px', letterSpacing: '1.5px', background: '#F07B1D', color: '#050608', border: 'none', cursor: 'pointer', boxShadow: '0 3px 14px rgba(240,123,29,.35)' }}
                      >
                        DROP IT
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Azeret Mono', monospace", fontSize: '11px', color: '#72788A', letterSpacing: '.5px', borderTop: '1px solid rgba(255,255,255,0.055)' }}>
                  <span>⏳ Waiting for {storyLines[storyLines.length - 1]?.authorHandle || 'someone'} to drop their line...</span>
                  <div style={{ marginLeft: 'auto', fontSize: '10px', color: '#3A3F4E' }}>in queue</div>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="story-side">
            {/* Reading Now */}
            <div style={{ background: '#0F1219', border: '1px solid rgba(255,255,255,0.055)', borderRadius: '12px', overflow: 'hidden', margin: '14px', marginBottom: '10px' }}>
              <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.055)' }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px', letterSpacing: '1px', color: '#EAE0CC', display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <span style={{ color: '#A855F7' }}>◎</span> Reading Now
                </span>
                <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '11px', color: '#18B574', fontWeight: 600 }}>
                  {(activeStoopData?.listeners?.length || 0) + 1}
                </span>
              </div>
              <div style={{ padding: '8px 14px' }}>
                {[
                  { handle: '@community', initials: 'BC', school: 'The Block', gradient: 'linear-gradient(135deg,#F07B1D,#C9950C)' },
                  { handle: '@readers', initials: 'RK', school: 'Howard', gradient: 'linear-gradient(135deg,#A855F7,#D44F2E)' },
                ].map(u => (
                  <div key={u.handle} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ position: 'relative', width: '28px', height: '28px', flexShrink: 0 }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: u.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: '11px', color: '#fff' }}>{u.initials}</div>
                      <span style={{ position: 'absolute', bottom: 0, right: 0, width: '7px', height: '7px', background: '#18B574', borderRadius: '50%', border: '2px solid #0F1219' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#C8BCA8' }}>{u.handle}</div>
                      <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', color: '#72788A' }}>{u.school}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Join Story Queue */}
            <div style={{ padding: '0 14px' }}>
              <button
                id="join-queue-btn"
                onClick={() => { setHasTurn(true); }}
                style={{
                  width: '100%', background: '#A855F7', color: '#fff',
                  fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px', letterSpacing: '1.5px',
                  border: 'none', borderRadius: '10px', padding: '12px', cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(168,85,247,.3)', transition: 'all .15s', marginBottom: '8px',
                }}
              >
                ✍🏾 Join the Story Queue
              </button>
            </div>

            {/* Drop to Block CTA */}
            <div className="drop-cta">
              <button className="drop-cta-btn" onClick={dropStoryToBlock}>
                📖 Drop Story to The Block
              </button>
              <div className="drop-cta-sub">
                All {[...new Set(storyLines.map(l => l.authorHandle))].length || 1} contributors credited
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Stoop — voice mode only (story shell is rendered above) */}
      {activeStoopData && !storyMode && (
        <div className={cn("border-b p-4", borderClass, isDark ? "bg-white/5" : "bg-gray-50")}>
          <div className={cn("flex items-center justify-between", storyMode ? "p-3 border-b" : "mb-4", borderClass)}>
            <div>
              {!storyMode && <p className={cn("text-[10px] uppercase tracking-wider mb-1", textVeryMutedClass)}>Live Now</p>}
              <h2 className={cn("font-display", storyMode ? "text-sm" : "text-lg", textClass)}>{activeStoopData.title}</h2>
            </div>
            <div className="flex items-center gap-2">
              {/* Mode toggle */}
              <div className="stoop-mode-pill">
                <button
                  className={cn("stoop-mode-btn", !storyMode && "on")}
                  onClick={storyMode ? exitStoryMode : undefined}
                >
                  <span className="w-[5px] h-[5px] rounded-full bg-[var(--jade)] flex-shrink-0" style={{ animation: 'livePip 1.8s ease-in-out infinite' }} />
                  Talk
                </button>
                <button
                  className={cn("stoop-mode-btn", storyMode && "on")}
                  onClick={!storyMode ? enterStoryMode : undefined}
                >
                  📖 Story
                </button>
              </div>
              {!storyMode && (
                <>
                  {connectionStatus.icon}
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className={cn("text-xs", textMutedClass)}>LIVE</span>
                </>
              )}
            </div>
          </div>

          {/* ── STORY TIME VIEW — now rendered at page top level ── */}
          {false && (
            <div className="story-shell" style={{ height: 'calc(100dvh - 120px)' }}>

              {/* Main story area */}
              <div className="story-main">

                {/* Story header */}
                <div className="story-hdr">
                  <div className="sh-top">
                    <div className="sh-icon">📖</div>
                    <div>
                      <div className="sh-title">{activeStoopData.title}</div>
                      <div className="sh-sub">Story Time · {storyLines.length} line{storyLines.length !== 1 ? 's' : ''} · {(activeStoopData.listeners?.length || 0) + 1} on The Stoop</div>
                    </div>
                  </div>
                  <div className="sh-contributors">
                    {storyLines.slice(0, 5).map((l, i) => (
                      <div key={l.id} className="contrib-av" style={{ background: GRADIENTS[i % GRADIENTS.length] }}>
                        {l.authorInitials}
                      </div>
                    ))}
                    {storyLines.length > 5 && <span className="contrib-more">+{storyLines.length - 5} contributors</span>}
                  </div>
                </div>

                {/* Story scroll */}
                <div className="story-scroll" ref={storyScrollRef}>
                  {storyLines.map((line, idx) => (
                    <div key={line.id}>
                      {idx > 0 && idx % 3 === 0 && (
                        <div className="story-divider">
                          <span>ROUND {Math.ceil(idx / 3)}</span>
                        </div>
                      )}
                      <div className="story-line">
                        <div className="sl-av" style={{ background: GRADIENTS[(line.id - 1) % GRADIENTS.length] }}>
                          {line.authorInitials}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="sl-meta">
                            <span className="sl-name">{line.authorName}</span>
                            <span className="sl-handle">{line.authorHandle}</span>
                            <span className="sl-num">{line.num}</span>
                          </div>
                          <div className={cn("sl-text", line.isOpening && "opening")}>{line.text}</div>
                          <div className="sl-plates">
                            <button className={cn("sl-plate-btn", line.plated && "plated")} onClick={() => plateLine(line.id)}>
                              🍽 {line.plates}
                            </button>
                            {line.isOpening && <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: 9, color: 'var(--char)' }}>Opening line</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Active fork cards */}
                  {storyForks.map(fork => (
                    <div key={fork.id} className="fork-card">
                      <div className="fork-lbl">⑂ Story Fork — {fork.closed ? 'Vote Closed' : 'Vote Now'}</div>
                      <div className="fork-question">{fork.question}</div>
                      <div className="fork-options">
                        {fork.options.map((opt, i) => (
                          <div key={i} className={cn("fork-opt", opt.voted && "voted")} onClick={() => !fork.closed && voteFork(fork.id, i)}>
                            <div className="fo-letter">{String.fromCharCode(65 + i)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="fo-text">{opt.text}</div>
                              <div className="fo-bar"><div className="fo-fill" style={{ width: `${opt.pct}%` }} /></div>
                            </div>
                            <div className="fo-votes">{opt.votes} votes</div>
                          </div>
                        ))}
                      </div>
                      {!fork.closed && (
                        <div className="fork-timer">
                          <span style={{ color: 'var(--story)' }}>30 seconds to vote</span>
                          <div className="ft-bar"><div className="ft-fill" style={{ width: '60%' }} /></div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Who has the stick */}
                  {!hasTurn && storyLines.length > 0 && (
                    <div className="stick-indicator">
                      <div className="si-av" style={{ background: GRADIENTS[storyLines.length % GRADIENTS.length] }}>
                        {storyLines[storyLines.length - 1]?.authorInitials}
                        <div className="si-glow" />
                      </div>
                      <div className="si-text">
                        <div className="si-name">{storyLines[storyLines.length - 1]?.authorName}</div>
                        <div className="si-sub">Passed the stick · You're up next</div>
                      </div>
                      <div className="si-stick">🪄</div>
                    </div>
                  )}
                </div>

                {/* Input area */}
                <div className="story-input-area">
                  {hasTurn ? (
                    <div className="yt-active">
                      <div className="yta-label">🎤 Your turn — The stick is yours</div>
                      <div className="yta-wrap">
                        <textarea
                          className="yta-input"
                          value={storyInput}
                          onChange={e => setStoryInput(e.target.value)}
                          placeholder="Continue the story... one line, your voice, your truth."
                          rows={2}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitStoryLine(); } }}
                        />
                        <div className="yta-actions">
                          <button className="yta-pass" onClick={() => { setHasTurn(false); toast('Stick passed forward'); }}>Pass 🪄</button>
                          <button className="yta-submit" onClick={submitStoryLine}>Drop It →</button>
                        </div>
                      </div>
                      <div className="yta-hint">One line. No more, no less. The stick passes when you drop it.</div>
                    </div>
                  ) : (
                    <div className="yt-not-turn">
                      <span style={{ fontSize: 14 }}>🪄</span>
                      <span>Waiting for the story to come to you...</span>
                      <div className="yt-queue">
                        <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: 9, background: 'rgba(240,123,29,.12)', color: 'var(--fire)', border: '1px solid rgba(240,123,29,.2)', borderRadius: 4, padding: '1px 7px' }}>In Queue</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Chat (mobile — collapsed) */}
                <div className="story-chat lg:hidden">
                  <div className="chat-label">Stoop Chat</div>
                  <div className="chat-msgs" ref={chatScrollRef}>
                    {chatMessages.map(m => (
                      <div key={m.id} className="cm">
                        <div className="cm-av" style={{ background: m.gradient }}>{m.av}</div>
                        <div><span className="cm-name">{m.name}</span><span className={cn("cm-text", m.hype && "hype")}>{m.text}</span></div>
                      </div>
                    ))}
                  </div>
                  <div className="chat-input-wrap">
                    <input
                      className="chat-input"
                      type="text"
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      placeholder="React to the story..."
                      onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                    />
                    <button className="chat-send" onClick={sendChat}>↑</button>
                  </div>
                </div>

              </div>{/* /story-main */}

              {/* Right sidebar (desktop only) */}
              <div className="story-side">

                {/* Story arc */}
                <div className="story-side-panel">
                  <div className="ssp-label">Story Arc</div>
                  <div className="sp-arc">
                    {[
                      { label: 'The Setup', state: storyLines.length >= 1 ? 'done' : 'pending' },
                      { label: 'Rising Action', state: storyLines.length >= 3 ? 'done' : storyLines.length >= 2 ? 'active' : 'pending' },
                      { label: 'The Turn', state: storyLines.length >= 5 ? 'done' : storyLines.length >= 4 ? 'active' : 'pending' },
                      { label: 'The Reveal', state: storyLines.length >= 7 ? 'done' : storyLines.length >= 6 ? 'active' : 'pending' },
                      { label: 'The Landing', state: storyLines.length >= 9 ? 'done' : storyLines.length >= 8 ? 'active' : 'pending' },
                    ].map((arc, i, arr) => (
                      <div key={arc.label}>
                        <div className="arc-part">
                          <div className={cn("arc-dot", arc.state)} />
                          <span className="arc-label" style={{ color: arc.state === 'done' ? 'var(--jade)' : arc.state === 'active' ? 'var(--fire)' : 'var(--char)' }}>
                            {arc.label}{arc.state === 'done' ? ' ✓' : arc.state === 'active' ? ' ← here' : ''}
                          </span>
                        </div>
                        {i < arr.length - 1 && <div className={cn("arc-line", arc.state === 'done' && "done")} />}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="story-side-panel">
                  <div className="ssp-label">Story Stats</div>
                  <div>
                    {[
                      { label: 'Lines Written', val: storyLines.length },
                      { label: 'Total Plates', val: storyLines.reduce((s, l) => s + l.plates, 0) + ' 🍽' },
                      { label: 'Contributors', val: [...new Set(storyLines.map(l => l.authorHandle))].length },
                      { label: 'Status', val: 'Active ●', live: true },
                    ].map(s => (
                      <div key={s.label} className="ss-row">
                        <span className="ss-lbl">{s.label}</span>
                        <span className={cn("ss-val", s.live && "live")}>{s.val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Chat (desktop) */}
                <div className="story-chat" style={{ flex: 1 }}>
                  <div className="chat-label">Stoop Chat</div>
                  <div className="chat-msgs" style={{ flex: 1, maxHeight: 'none' }}>
                    {chatMessages.map(m => (
                      <div key={m.id} className="cm">
                        <div className="cm-av" style={{ background: m.gradient }}>{m.av}</div>
                        <div><span className="cm-name">{m.name}</span><span className={cn("cm-text", m.hype && "hype")}>{m.text}</span></div>
                      </div>
                    ))}
                  </div>
                  <div className="chat-input-wrap">
                    <input
                      className="chat-input"
                      type="text"
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      placeholder="React to the story..."
                      onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                    />
                    <button className="chat-send" onClick={sendChat}>↑</button>
                  </div>
                </div>

                {/* Drop CTA */}
                <div className="drop-cta">
                  <button className="drop-cta-btn" onClick={dropStoryToBlock}>
                    📖 Drop Story to The Block
                  </button>
                  <div className="drop-cta-sub">
                    All {[...new Set(storyLines.map(l => l.authorHandle))].length || 1} contributors credited · Saved to BLVX forever
                  </div>
                </div>

              </div>{/* /story-side */}
            </div>
          )}{/* /story-shell */}

          {/* ── VOICE MODE (existing) ── */}
          {!storyMode && (
          <div className="p-4">

          {/* Connection Status */}
          {connectionError && (
            <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 text-xs text-red-400">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">Connection Error</span>
              </div>
              <p className="text-red-400/70 text-[10px]">{connectionError}</p>
            </div>
          )}
          
          {/* Audio Blocked Warning */}
          {audioBlocked && (
            <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 text-xs text-amber-400">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium">Can't hear anyone? Tap to enable audio</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={resumeAudio}
                  className="h-7 px-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400"
                >
                  🔊 Enable Audio
                </Button>
              </div>
            </div>
          )}
          
          {/* Speakers */}
          {(() => {
            const hasAnyVideo = activeStoopData.speaker_details?.some(s => videoTracks[s.user_id]);
            return (
            <div className="mb-4">
              <p className={cn("text-[10px] uppercase tracking-wider mb-2", isDark ? "text-white/40" : "text-gray-500")}>
                Speakers ({activeStoopData.speaker_details?.length || 1}/{activeStoopData.max_speakers || 4})
              </p>
              <div className={hasAnyVideo ? "speakers-grid video-mode" : "speakers-grid audio-mode"}>
                {activeStoopData.speaker_details?.map((speaker) => {
                  const hasVideo = !!videoTracks[speaker.user_id];
                  const isLocal = speaker.user_id === user.user_id;
                  return (
                    <div key={speaker.user_id} className={cn("speaker-tile flex flex-col items-center", hasVideo && "has-video")}>
                      <div className="relative" style={hasVideo ? { width: '100%', height: '100%' } : undefined}>
                        {hasVideo ? (
                          <>
                            <div className={cn("speaker-video", isLocal && "is-local")}>
                              <VideoTile stream={videoTracks[speaker.user_id]} />
                            </div>
                            {/* Name overlay on video */}
                            <span style={{
                              position: 'absolute', bottom: '6px', left: '8px',
                              fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1px',
                              color: 'rgba(255,255,255,0.9)', background: 'rgba(0,0,0,0.5)',
                              padding: '2px 6px', borderRadius: '4px',
                            }}>
                              {isLocal ? 'You' : speaker.name?.split(' ')[0]}
                            </span>
                            {/* Mic indicator on video */}
                            {isSpeakerLive(speaker.user_id) && (
                              <div className="absolute top-2 right-2 bg-green-500 rounded-full p-0.5">
                                <Volume2 className="h-2.5 w-2.5 text-black" />
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <Avatar className={cn(
                              "h-12 w-12 border-2 transition-all duration-300",
                              isSpeakerLive(speaker.user_id)
                                ? "border-green-500 shadow-lg shadow-green-500/30 ring-2 ring-green-500/20"
                                : "border-white/30"
                            )}>
                              <AvatarImage src={speaker.picture} />
                              <AvatarFallback className="bg-white/10 text-sm">{speaker.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            {isSpeakerLive(speaker.user_id) && (
                              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5">
                                <Volume2 className="h-2.5 w-2.5 text-black" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {!hasVideo && (
                        <>
                          <span className={cn("text-[10px] mt-1", isDark ? "text-white/60" : "text-gray-600")}>{speaker.name?.split(' ')[0]}</span>
                          {isSpeakerLive(speaker.user_id) && (
                            <span className="text-[8px] text-green-500 mt-0.5 animate-pulse">LIVE</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}
          
          {/* Listeners */}
          {activeStoopData.listeners?.length > 0 && (
            <div className="mb-4">
              <p className={cn("text-[10px] uppercase tracking-wider mb-2", isDark ? "text-white/40" : "text-gray-500")}>
                Listening ({activeStoopData.listeners?.length || 0})
              </p>
              <div className="flex -space-x-2">
                {activeStoopData.listeners?.slice(0, 8).map((listenerId, idx) => (
                  <Avatar key={listenerId} className="h-8 w-8 border-2 border-black">
                    <AvatarFallback className="bg-white/10 text-xs">{idx + 1}</AvatarFallback>
                  </Avatar>
                ))}
                {activeStoopData.listeners?.length > 8 && (
                  <div className="h-8 w-8 rounded-full bg-white/10 border-2 border-black flex items-center justify-center text-[10px]">
                    +{activeStoopData.listeners.length - 8}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Speaker Queue - "Next Up" */}
          {speakerQueue.length > 0 && (
            <div className="mb-4">
              <p className={cn("text-[10px] uppercase tracking-wider mb-2", isDark ? "text-white/40" : "text-gray-500")}>
                Next Up ({speakerQueue.length})
              </p>
              <div className="space-y-2">
                {speakerQueue.map((req) => (
                  <div 
                    key={req.user_id}
                    className={cn(
                      "flex items-center justify-between p-2 rounded-lg",
                      isDark ? "bg-white/5" : "bg-gray-50"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8 border border-white/20">
                        <AvatarImage src={req.picture} />
                        <AvatarFallback className="bg-white/10 text-xs">{req.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className={cn("text-xs font-medium", textClass)}>{req.name}</p>
                        <p className={cn("text-[10px]", textMutedClass)}>
                          {req.expression_emoji} "{req.expression_text}"
                        </p>
                      </div>
                    </div>
                    
                    {/* Host controls */}
                    {activeStoopData.host_id === user.user_id && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => approveSpeaker(req.user_id)}
                          className="h-7 px-2 text-green-500 hover:text-green-400 hover:bg-green-500/10"
                        >
                          ✓
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => denySpeaker(req.user_id)}
                          className="h-7 px-2 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          ✕
                        </Button>
                      </div>
                    )}
                    
                    {/* Cancel own request */}
                    {req.user_id === user.user_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelMicRequest}
                        className="h-7 px-2 text-red-500 hover:text-red-400"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Request Mic Button (for listeners) */}
          {!isSpeaker && activeStoopData.host_id !== user.user_id && !hasRequestedMic && (
            <div className="mb-4 relative">
              <Button
                onClick={() => setShowMicRequestMenu(!showMicRequestMenu)}
                className={cn(
                  "w-full text-sm font-medium",
                  isDark ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30" 
                         : "bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200"
                )}
              >
                🎤 I wanna speak
              </Button>
              
              {/* Expression Menu */}
              {showMicRequestMenu && (
                <div className={cn(
                  "absolute bottom-full left-0 right-0 mb-2 p-2 rounded-lg border shadow-lg z-10",
                  isDark ? "bg-zinc-900 border-white/10" : "bg-white border-gray-200"
                )}>
                  <p className={cn("text-[10px] uppercase tracking-wider mb-2 px-2", textMutedClass)}>
                    How you wanna come in?
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {MIC_EXPRESSIONS.map((expr) => (
                      <button
                        key={expr.id}
                        onClick={() => requestMic(expr.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors",
                          isDark ? "hover:bg-white/10" : "hover:bg-gray-100"
                        )}
                      >
                        <span>{expr.emoji}</span>
                        <span className={textClass}>{expr.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Already requested - show status */}
          {hasRequestedMic && !isSpeaker && (
            <div className={cn(
              "mb-4 p-3 rounded-lg text-center text-sm",
              isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-700"
            )}>
              🎤 You're in line — waiting for the host
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelMicRequest}
                className="ml-2 text-red-500 hover:text-red-400"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Remote Participants (from LiveKit) */}
          {participants.length > 0 && (
            <div className="mb-4">
              <p className={cn("text-[10px] uppercase tracking-wider mb-2", isDark ? "text-white/40" : "text-gray-500")}>
                Connected ({participants.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {participants.map((p) => (
                  <div
                    key={p.identity}
                    className={cn(
                      "px-2 py-1 text-[10px] rounded-full border",
                      p.isSpeaking
                        ? "bg-green-500/20 border-green-500/50 text-green-400"
                        : isDark ? "bg-white/5 border-white/20 text-white/60" : "bg-gray-100 border-gray-300 text-gray-600"
                    )}
                  >
                    {p.name} {p.audioEnabled ? '🎤' : ''}{p.videoEnabled ? '📹' : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Audio Settings — device picker + mode toggle, only for speakers */}
          {isSpeaker && isConnected && (
            <div className={cn("mb-4 p-3 border rounded-sm", isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-gray-50")}>
              <p className={cn("text-[10px] uppercase tracking-widest mb-3 font-display", textVeryMutedClass)}>Audio Settings</p>

              {/* Input Device Picker */}
              <div className="mb-3">
                <label className={cn("text-[10px] block mb-1", textMutedClass)}>Input Device</label>
                <Select
                  value={selectedDeviceId || 'default'}
                  onValueChange={async (deviceId) => {
                    try {
                      await switchAudioDevice(deviceId === 'default' ? '' : deviceId);
                      toast.success('Input device switched');
                    } catch (e) {
                      toast.error('Failed to switch device');
                    }
                  }}
                  onOpenChange={(open) => { if (open) enumerateDevices(); }}
                >
                  <SelectTrigger className={cn("h-8 text-xs rounded-sm", isDark ? "bg-transparent border-white/20 text-white/80" : "bg-white border-gray-300")}>
                    <SelectValue placeholder="Default microphone" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {availableDevices.length === 0 && (
                      <SelectItem value="default">Default microphone</SelectItem>
                    )}
                    {availableDevices.map((device, idx) => (
                      <SelectItem key={device.deviceId || `dev-${idx}`} value={device.deviceId || 'default'}>
                        {device.label || `Microphone ${idx + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Voice / Music Mode Toggle */}
              <div>
                <label className={cn("text-[10px] block mb-1", textMutedClass)}>Audio Mode</label>
                <div className="flex gap-1.5">
                  <button
                    onClick={async () => {
                      try {
                        await setAudioMode('voice');
                        toast.success('Voice mode — speech optimized');
                      } catch (e) {
                        toast.error('Failed to switch mode');
                      }
                    }}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-sm transition-colors",
                      audioMode === 'voice'
                        ? isDark ? "bg-white text-black" : "bg-black text-white"
                        : isDark ? "bg-white/10 text-white/50 hover:bg-white/20 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                    )}
                  >
                    <Mic className="h-3 w-3" />
                    Voice
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await setAudioMode('music');
                        toast.success('Music mode — raw audio, no processing');
                      } catch (e) {
                        toast.error('Failed to switch mode');
                      }
                    }}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-sm transition-colors",
                      audioMode === 'music'
                        ? "bg-amber-500 text-black"
                        : isDark ? "bg-white/10 text-white/50 hover:bg-white/20 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                    )}
                  >
                    <Music className="h-3 w-3" />
                    Music
                  </button>
                </div>
                {audioMode === 'music' && (
                  <p className={cn("text-[10px] mt-1.5", textVeryMutedClass)}>
                    Echo cancel disabled — route your DAW here.{' '}
                    <a href="/help/stoop-daw-routing" className="text-amber-500 underline">Setup guide →</a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Mic Error Message */}
          {micError && (
            <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-xs text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{micError}</span>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleMic}
              disabled={!isSpeaker || !isConnected}
              className={cn(
                "flex-1 text-xs transition-all",
                isLocalMicActive
                  ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                  : isSpeaker 
                    ? isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    : isDark ? "text-white/50" : "text-gray-400"
              )}
              data-testid="stoop-mic-btn"
            >
              {isLocalMicActive ? (
                <>
                  <Mic className="h-4 w-4 mr-2 animate-pulse" />
                  On Mic
                </>
              ) : (
                <>
                  <MicOff className="h-4 w-4 mr-2" />
                  {isSpeaker ? 'Turn On Mic' : 'Listening'}
                </>
              )}
            </Button>
            {/* Camera toggle — speakers only */}
            {isSpeaker && (
              <Button
                onClick={toggleCamera}
                variant="ghost"
                size="sm"
                className={cn(
                  "text-xs",
                  isCameraOn
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                    : isDark ? "text-white/50 hover:text-white" : "text-gray-400 hover:text-gray-900"
                )}
                data-testid="stoop-camera-btn"
              >
                {isCameraOn ? '📹 Cam On' : '📷 Camera'}
              </Button>
            )}
            <Button
              onClick={leaveStoop}
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-400 text-xs"
              data-testid="stoop-leave-btn"
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              Leave
            </Button>
            {activeStoopData.host_id === user.user_id && (
              <>
                {/* Speaker Limit Control */}
                <div className="flex items-center gap-2">
                  <Settings className="h-3 w-3 text-white/40" />
                  <Select
                    value={String(activeStoopData.max_speakers || 4)}
                    onValueChange={async (value) => {
                      try {
                        await axios.put(
                          `${API}/api/stoop/${activeStoopId}/settings?max_speakers=${value}`,
                          {},
                          { withCredentials: true }
                        );
                        toast.success(`Max speakers set to ${value}`);
                      } catch (error) {
                        toast.error('Failed to update settings');
                      }
                    }}
                  >
                    <SelectTrigger className="h-7 w-20 text-[10px] bg-transparent border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {[2, 3, 4, 5, 6, 8, 10].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} max</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={endStoop}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-400 text-xs"
                  data-testid="stoop-end-btn"
                >
                  End Stoop
                </Button>
              </>
            )}
          </div>
          
          {/* LiveKit Status Debug */}
          <div className={cn("mt-3 p-2 bg-muted rounded text-[10px] space-y-1", isDark ? "text-white/50" : "text-gray-600")}>
            <div className="flex items-center justify-between">
              <span>
                LiveKit: {connectionState} • 
                Participants: {participants.length} • 
                Role: {isSpeaker ? 'Speaker' : 'Listener'} •
                Mic: {isLocalMicActive ? '🎤 ON' : '🔇 OFF'}
              </span>
              <button 
                onClick={() => {
                  debugRoomState();
                  toast.info('Check browser console for debug info');
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Debug
              </button>
            </div>
          </div>
          </div>
          )}{/* /voice mode */}

        </div>
      )}

      {/* Stoop List — hidden when Story Time is active */}
      {!storyMode && (loading ? (
        <div className="p-4 space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : stoops.length === 0 ? (
        <div className="text-center py-16 px-6">
          <Radio className={cn("h-12 w-12 mx-auto mb-4", isDark ? "text-white/20" : "text-gray-300")} />
          <p className={cn("text-sm mb-2", textMutedClass)}>No Stoops live right now</p>
          <p className={cn("text-xs", isDark ? "text-white/30" : "text-gray-400")}>Start one and gather the people</p>
        </div>
      ) : (
        <div className={cn("divide-y", borderClass)}>
          {stoops.map((stoop) => (
            <div
              key={stoop.stoop_id}
              className={cn("p-4 cursor-pointer transition-colors", hoverBgClass)}
              onClick={() => joinStoop(stoop.stoop_id)}
              data-testid={`stoop-${stoop.stoop_id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className={cn("font-medium text-sm", textClass)}>{stoop.title}</h3>
                  <p className={cn("text-xs", textMutedClass)}>Hosted by {stoop.host?.name}</p>
                </div>
                <div className={cn("flex items-center gap-1 text-[10px]", textVeryMutedClass)}>
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  LIVE
                </div>
              </div>
              <div className={cn("flex items-center gap-4 text-xs", textVeryMutedClass)}>
                <span className="flex items-center gap-1">
                  <Mic className="h-3 w-3" />
                  {stoop.speaker_count || 1} speaking
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {stoop.listener_count || 0} listening
                </span>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Create Stoop Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border border-border sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-display text-sm tracking-widest uppercase">Start a Stoop</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              value={newStoopTitle}
              onChange={(e) => setNewStoopTitle(e.target.value)}
              placeholder="What's the topic?"
              className="bg-transparent border-white/20 focus:border-white rounded-none"
              data-testid="stoop-title-input"
            />
            <Button
              onClick={createStoop}
              disabled={!newStoopTitle.trim()}
              className="w-full bg-white text-black hover:bg-white/90 rounded-none font-display tracking-wider"
              data-testid="create-stoop-submit"
            >
              Go Live
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
